import React, { useState, useEffect } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Users, Trophy, Plus, Trash2, Edit } from 'lucide-react';
import { colors } from '../../constants/theme';
import { playersService, competitionService } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';

const FileUpload = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [competitionResult, setCompetitionResult] = useState(null);
  const [step, setStep] = useState('upload');
  const [players, setPlayers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    birthDate: '',
    category: 'infantil'
  });

  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    try {
      const response = await playersService.getAll({ limit: 100 });
      if (response.success) {
        setPlayers(response.data.players);
      }
    } catch (error) {
      console.error('Erro ao carregar jogadores:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    setError(null);
    setLoading(true);

    try {
      console.log('📤 Fazendo upload do arquivo:', file.name);
      const response = await playersService.uploadCsv(file);
      
      if (response.success) {
        setUploadResult(response.data);
        setStep('competition');
        await loadPlayers(); // Recarregar lista
        console.log('✅ Upload concluído:', response.data);
      }
    } catch (error) {
      console.error('❌ Erro no upload:', error);
      setError(error.error?.message || 'Erro ao fazer upload do arquivo');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await playersService.create(newPlayer);
      if (response.success) {
        setNewPlayer({ name: '', birthDate: '', category: 'infantil' });
        setShowAddForm(false);
        await loadPlayers();
        alert('Jogador adicionado com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao adicionar jogador:', error);
      setError(error.error?.message || 'Erro ao adicionar jogador');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Tem certeza que deseja remover este jogador?')) {
      return;
    }

    try {
      await playersService.delete(playerId);
      await loadPlayers();
      alert('Jogador removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover jogador:', error);
      alert('Erro ao remover jogador: ' + (error.error?.message || 'Erro desconhecido'));
    }
  };

  const handleCreateCompetition = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🏆 Criando competição...');
      
      const competitionData = {
        name: `Campeonato Goleiro Raiz ${new Date().getFullYear()}`,
        startDate: new Date().toISOString().split('T')[0],
        playersPerGroup: 4
      };

      const createResponse = await competitionService.create(competitionData);
      console.log('✅ Competição criada:', createResponse);
      
      if (createResponse.success) {
        console.log('🎲 Sorteando chaves...');
        
        const drawResponse = await competitionService.drawGroups();
        console.log('✅ Chaves sorteadas:', drawResponse);
        
        if (drawResponse.success) {
          setCompetitionResult(drawResponse.data);
          setStep('groups');
        }
      }
    } catch (error) {
      console.error('❌ Erro ao criar competição:', error);
      setError(error.error?.message || 'Erro ao criar competição');
    } finally {
      setLoading(false);
    }
  };

  const handleViewGroups = async () => {
    setLoading(true);
    try {
      const response = await competitionService.getGroups();
      if (response.success) {
        setCompetitionResult(response.data);
        setStep('groups');
      }
    } catch (error) {
      console.error('Erro ao carregar chaves:', error);
      setError('Erro ao carregar chaves');
    } finally {
      setLoading(false);
    }
  };

  const getPlayerCountByCategory = () => {
    const counts = {
      mirim: 0,
      infantil: 0,
      juvenil: 0,
      adulto: 0,
      master: 0
    };
    
    players.forEach(player => {
      if (counts.hasOwnProperty(player.category)) {
        counts[player.category]++;
      }
    });
    
    return counts;
  };

  const playerCounts = getPlayerCountByCategory();

  if (loading && players.length === 0) {
    return <LoadingSpinner size="medium" text="Carregando..." />;
  }

  return (
    <div className="space-y-6">
      {/* Lista de Jogadores Inscritos */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
            Jogadores Inscritos ({players.length})
          </h2>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: colors.primary }}
          >
            <Plus className="h-4 w-4" />
            <span>Adicionar Jogador</span>
          </button>
        </div>

        {/* Estatísticas por categoria */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {Object.entries(playerCounts).map(([category, count]) => (
            <div key={category} className={`p-3 rounded-lg border-2 ${count >= 3 ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
              <div className="text-center">
                <p className="text-sm font-medium capitalize">{category}</p>
                <p className={`text-2xl font-bold ${count >= 3 ? 'text-green-600' : 'text-red-600'}`}>
                  {count}
                </p>
                <p className="text-xs text-gray-500">
                  {count >= 3 ? '✓ Suficiente' : '⚠️ Mín. 3'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Formulário de adição */}
        {showAddForm && (
          <form onSubmit={handleAddPlayer} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-3">Adicionar Novo Jogador</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={newPlayer.name}
                  onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ focusRingColor: colors.primary }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento</label>
                <input
                  type="date"
                  value={newPlayer.birthDate}
                  onChange={(e) => setNewPlayer({ ...newPlayer, birthDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ focusRingColor: colors.primary }}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select
                  value={newPlayer.category}
                  onChange={(e) => setNewPlayer({ ...newPlayer, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                  style={{ focusRingColor: colors.primary }}
                >
                  <option value="mirim">Mirim</option>
                  <option value="infantil">Infantil</option>
                  <option value="juvenil">Juvenil</option>
                  <option value="adulto">Adulto</option>
                  <option value="master">Master</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-white rounded hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {loading ? 'Adicionando...' : 'Adicionar'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        {/* Lista de jogadores */}
        {players.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nenhum jogador inscrito ainda</p>
            <p className="text-sm">Adicione jogadores manualmente ou faça upload de um CSV</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Nome</th>
                  <th className="px-4 py-2 text-left">Data Nascimento</th>
                  <th className="px-4 py-2 text-left">Categoria</th>
                  <th className="px-4 py-2 text-center">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {players.map((player) => (
                  <tr key={player.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{player.name}</td>
                    <td className="px-4 py-2">{new Date(player.birthDate).toLocaleDateString('pt-BR')}</td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-1 rounded text-xs font-medium capitalize" 
                            style={{ backgroundColor: colors.primary, color: 'white' }}>
                        {player.category}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleDeletePlayer(player.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Remover jogador"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload de arquivo CSV */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>
          Upload em Lote (CSV)
        </h2>

        {error && (
          <ErrorMessage message={error} className="mb-4" />
        )}

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload da Lista de Inscritos (CSV)
            </label>
            <div className="flex items-center justify-center w-full">
              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer ${
                uploadResult ? 'border-green-300 bg-green-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              }`}>
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {uploadResult ? (
                    <CheckCircle className="w-8 h-8 mb-4 text-green-500" />
                  ) : (
                    <Upload className="w-8 h-8 mb-4 text-gray-500" />
                  )}
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">
                      {uploadResult ? 'Arquivo carregado com sucesso!' : 'Clique para fazer upload'}
                    </span>
                    {!uploadResult && ' ou arraste o arquivo'}
                  </p>
                  <p className="text-xs text-gray-500">Apenas arquivos CSV</p>
                </div>
                <input 
                  type="file" 
                  accept=".csv" 
                  onChange={handleFileUpload}
                  className="hidden" 
                  disabled={!!uploadResult}
                />
              </label>
            </div>

            {uploadedFile && (
              <div className="mt-3 flex items-center text-sm text-green-600">
                <FileText className="w-4 h-4 mr-2" />
                Arquivo carregado: {uploadedFile.name}
              </div>
            )}
          </div>

          {uploadResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <h3 className="font-medium text-green-800">Upload Concluído</h3>
              </div>
              <div className="text-sm text-green-700">
                <p>✅ Jogadores criados: {uploadResult.playersCreated}</p>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <p className="text-orange-600">⚠️ Erros: {uploadResult.errors.length}</p>
                )}
              </div>
            </div>
          )}

          {/* Formato esperado do CSV */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">Formato do CSV esperado:</h4>
            <div className="text-xs text-blue-700 font-mono bg-white p-2 rounded border">
              nome,data_nascimento,categoria<br />
              João Silva,2010-03-15,infantil<br />
              Pedro Santos,2011-07-22,infantil<br />
              Rafael Lima,2007-05-12,juvenil<br />
              Carlos Eduardo,1995-08-14,adulto<br />
              José Almeida,1975-03-10,master
            </div>
            <p className="text-sm text-blue-600 mt-2">
              <strong>Categorias válidas:</strong> infantil, juvenil, adulto, master<br />
              <strong>Mínimo:</strong> 3 jogadores por categoria
            </p>
          </div>
        </div>
      </div>

      {/* Botão para criar competição */}
      {players.length >= 12 && step !== 'groups' && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-bold mb-4" style={{ color: colors.primary }}>
            Criar Competição
          </h3>
          
          {Object.values(playerCounts).every(count => count >= 3) ? (
            <div>
              <p className="text-gray-600 mb-4">
                ✅ Todas as categorias têm jogadores suficientes! Você pode criar a competição agora.
              </p>
              <button
                onClick={handleCreateCompetition}
                disabled={loading}
                className="w-full px-6 py-3 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center space-x-2"
                style={{ backgroundColor: colors.primary }}
              >
                <Trophy className="w-5 h-5" />
                <span>{loading ? 'Criando...' : 'Criar Competição e Sortear Chaves'}</span>
              </button>
            </div>
          ) : (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <AlertCircle className="w-5 h-5 text-orange-500 mr-2" />
                <h4 className="font-medium text-orange-800">Jogadores Insuficientes</h4>
              </div>
              <p className="text-sm text-orange-700">
                Algumas categorias precisam de mais jogadores. Mínimo de 3 por categoria.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Resultado da competição e chaves */}
      {competitionResult && step === 'groups' && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <Trophy className="w-6 h-6 mr-2" style={{ color: colors.primary }} />
            <h3 className="text-lg font-bold" style={{ color: colors.primary }}>
              Competição Criada com Sucesso!
            </h3>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-2">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="font-medium text-green-800">
                Chaves sorteadas e partidas geradas!
              </span>
            </div>
            {competitionResult.totalMatches && (
              <p className="text-sm text-green-700">
                📊 Total de partidas criadas: {competitionResult.totalMatches}
              </p>
            )}
          </div>

          {/* Mostrar as chaves sorteadas */}
          <div className="space-y-6">
            <h4 className="font-semibold text-gray-800">Chaves Sorteadas:</h4>
            
            {Object.entries(competitionResult.groups || {}).map(([category, groups]) => (
              <div key={category} className="border rounded-lg p-4">
                <h5 className="font-medium text-lg mb-4 capitalize" style={{ color: colors.primary }}>
                  Categoria {category}
                </h5>
                
                {/* Mostrar múltiplos grupos */}
                <div className="grid gap-4">
                  {Object.entries(groups).map(([groupName, players]) => (
                    <div key={groupName} className="bg-gray-50 rounded-lg p-3">
                      <h6 className="font-medium mb-2 text-center" style={{ color: colors.primary }}>
                        {groupName.replace('grupo', 'Grupo ')}
                      </h6>
                      <div className="grid grid-cols-2 gap-2">
                        {players.map((player, index) => (
                          <div key={player.id} className="flex items-center space-x-2 p-2 bg-white rounded">
                            <Users className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium">{player.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              🎯 <strong>Próximos passos:</strong><br />
              1. Vá para "Iniciar Partidas" para começar os jogos<br />
              2. Acompanhe os resultados na aba "Chaves e Rankings" como espectador<br />
              3. As fases seguintes serão liberadas automaticamente conforme as partidas forem finalizadas
            </p>
          </div>
        </div>
      )}

      {/* Botão para ver chaves existentes */}
      {!competitionResult && players.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <button
            onClick={handleViewGroups}
            className="w-full px-6 py-3 bg-gray-100 text-gray-800 rounded-lg font-medium hover:bg-gray-200"
          >
            Ver Chaves Existentes
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;